import { RefObject, useEffect, useRef } from 'react';
import type ReactPlayer from 'react-player';
import { issueCloudFrontSignedCookies, shouldUseCredentialsForMediaUrl } from '../api';

type FragmentLoaded = { frag?: { type?: string } };
type LoadListener = (event?: unknown, data?: FragmentLoaded) => void;
type HlsPlayback = {
  stopLoad: () => void;
  startLoad: (position: number) => void;
  loadSource: (url: string) => void;
  on: (event: string, listener: LoadListener) => void;
  off: (event: string, listener: LoadListener) => void;
};
type PlaybackError = {
  type?: string;
  details?: string;
  fatal?: boolean;
  response?: { code?: number };
  frag?: { type?: string };
};

export const useCloudFrontPlaybackRecovery = (
  url: string,
  playerRef: RefObject<ReactPlayer>,
  playing: boolean,
) => {
  const attempts = useRef(0);
  const refreshing = useRef(false);
  const stopped = useRef(false);
  const cleanup = useRef(() => {});
  const generation = useRef(0);
  const playingRef = useRef(playing);
  playingRef.current = playing;

  useEffect(() => {
    attempts.current = 0;
    refreshing.current = false;
    stopped.current = false;
    return () => {
      generation.current++;
      cleanup.current();
    };
  }, [url]);

  const onError = async (_event: unknown, data?: PlaybackError, hls?: HlsPlayback) => {
    if (stopped.current || !url || !shouldUseCredentialsForMediaUrl(url)) return;
    const player = playerRef.current;
    if (!player) return;
    const media = player.getInternalPlayer?.() as HTMLVideoElement | undefined;
    // A CORS-blocked request exposes status 0, so an HTTP status alone is insufficient.
    const networkFailure = data
      ? data.type === 'networkError' &&
        (data.response?.code === 403 ||
          ((!data.response?.code) && /Load(Error|TimeOut)$/.test(data.details || '')))
      : media?.error?.code === 2 || media?.error?.code === 4;
    if (!networkFailure || refreshing.current) return;

    hls?.stopLoad();
    const fail = () => {
      stopped.current = true;
      cleanup.current();
      media?.pause?.();
      if (!hls && media) {
        media.removeAttribute('src');
        media.load();
      }
    };
    if (attempts.current >= 3) {
      fail();
      return;
    }

    refreshing.current = true;
    const currentGeneration = generation.current;
    const position = Math.max(0, player.getCurrentTime() || 0);
    const isCurrent = () => generation.current === currentGeneration && playerRef.current === player;
    const recovered: LoadListener = (_event, loaded) => {
      if (!isCurrent()) return;
      // An unrelated audio/subtitle fragment must not reset a failing video recovery.
      if (loaded?.frag?.type && loaded.frag.type !== (data?.frag?.type || 'main')) return;
      attempts.current = 0;
      cleanup.current();
    };
    cleanup.current();
    while (attempts.current < 3) {
      if (attempts.current > 0) {
        await new Promise<void>((resolve) => {
          const timeout = window.setTimeout(resolve, 10000);
          cleanup.current = () => { window.clearTimeout(timeout); resolve(); };
        });
        if (!isCurrent()) return;
      }
      attempts.current++;
      try {
        // This recovery owns the retries; the background timer must not add more.
        await issueCloudFrontSignedCookies(url, { forceRefresh: true, retryOnFailure: false });
        if (!isCurrent()) return;
        refreshing.current = false;
        cleanup.current();
        if (hls) {
          hls.on('hlsFragLoaded', recovered);
          cleanup.current = () => hls.off('hlsFragLoaded', recovered);
          if (data?.details === 'manifestLoadError' || data?.details === 'manifestLoadTimeOut') {
            hls.loadSource(url);
          }
          hls.startLoad(position);
        } else if (media) {
          const playbackRate = media.playbackRate;
          const restorePosition = () => {
            if (!isCurrent()) return;
            media.currentTime = position;
            media.playbackRate = playbackRate;
            if (playingRef.current) void media.play().catch(() => {});
          };
          const finishNativeRecovery = () => {
            restorePosition();
            recovered();
          };
          media.addEventListener('loadedmetadata', restorePosition, { once: true });
          media.addEventListener('canplay', finishNativeRecovery, { once: true });
          cleanup.current = () => {
            media.removeEventListener('loadedmetadata', restorePosition);
            media.removeEventListener('canplay', finishNativeRecovery);
          };
          media.load();
        }
        return;
      } catch {
        if (!isCurrent()) return;
      }
    }
    refreshing.current = false;
    fail();
  };

  return { onError };
};
